import type { FeedbackClarificationDto, FeedbackClarificationListResponse } from "@keydials/api-client";

/**
 * The api-client calls the clarification indicator needs, injected so the logic below can be unit-tested
 * without a DOM or a live API (same approach as workspaceLoad.ts).
 */
export interface ClarificationDeps {
  list: (token: string) => Promise<FeedbackClarificationListResponse>;
  reply: (token: string, id: string, message: string) => Promise<void>;
  markRead: (token: string, id: string) => Promise<void>;
}

export const EMPTY_CLARIFICATIONS: FeedbackClarificationListResponse = { items: [], unreadCount: 0 };

/**
 * Best-effort load. A failure leaves the indicator empty rather than breaking the header — this runs on every
 * sign-in and every pushed event, and a feedback badge is never worth a broken page.
 */
export async function loadClarifications(
  deps: Pick<ClarificationDeps, "list">,
  token: string | null,
): Promise<FeedbackClarificationListResponse> {
  if (!token) {
    return EMPTY_CLARIFICATIONS;
  }
  try {
    return await deps.list(token);
  } catch {
    return EMPTY_CLARIFICATIONS;
  }
}

/**
 * The threads this user can actually answer: ones they submitted, where the admin spoke last. A thread where
 * they already replied is still shown in the panel, just without a reply prompt.
 */
export function answerableThreads(items: readonly FeedbackClarificationDto[]): FeedbackClarificationDto[] {
  return items.filter((t) => t.myRole === "Submitter" && t.lastMessageBy === "Admin");
}

/** Optimistic state after the caller reads one thread — clears its unread flag and decrements the tally. */
export function markReadLocally(
  state: FeedbackClarificationListResponse,
  userFeedbackId: string,
): FeedbackClarificationListResponse {
  const target = state.items.find((t) => t.userFeedbackId === userFeedbackId);
  if (!target?.isUnread) {
    return state;
  }
  return {
    items: state.items.map((t) => (t.userFeedbackId === userFeedbackId ? { ...t, isUnread: false } : t)),
    unreadCount: Math.max(0, state.unreadCount - 1),
  };
}

/**
 * Optimistic state after the caller sends a reply: the message joins the thread, they become the last speaker,
 * and the thread is no longer unread for them.
 */
export function applyReplyLocally(
  state: FeedbackClarificationListResponse,
  userFeedbackId: string,
  message: string,
  sentAtUtc: string,
): FeedbackClarificationListResponse {
  const read = markReadLocally(state, userFeedbackId);
  return {
    ...read,
    items: read.items.map((t) =>
      t.userFeedbackId === userFeedbackId
        ? {
            ...t,
            lastMessageBy: "Submitter",
            lastMessageAtUtc: sentAtUtc,
            messages: [...t.messages, { from: "Submitter", body: message, createdAtUtc: sentAtUtc }],
          }
        : t,
    ),
  };
}
